import { z } from 'zod';

// Helper para validar URLs (aceita com ou sem protocolo)
const isValidUrl = (url: string): boolean => {
  if (!url || url === '') return true;
  
  // Se não tiver protocolo, adiciona temporariamente para validar formato
  const urlToTest = url.includes('://') ? url : `https://${url}`;
  
  try {
    new URL(urlToTest);
    return true;
  } catch {
    return false;
  }
};

export const loginSchema = z.object({
  email: z.string().email('Email inválido').trim(),
  password: z.string().min(6, 'A senha deve ter pelo menos 6 caracteres'),
});

export const signupSchema = z.object({
  nome: z.string()
    .trim()
    .min(2, 'O nome deve ter pelo menos 2 caracteres')
    .max(100, 'O nome deve ter no máximo 100 caracteres'),
  email: z.string()
    .email('Email inválido')
    .trim()
    .max(255, 'O email deve ter no máximo 255 caracteres'),
  password: z.string()
    .min(8, 'A senha deve ter pelo menos 8 caracteres')
    .max(128, 'A senha deve ter no máximo 128 caracteres')
    .regex(/[0-9]/, 'A senha deve conter pelo menos 1 número')
    .regex(/[!@#$%^&*(),.?":{}|<>]/, 'A senha deve conter pelo menos 1 símbolo (!@#$%...)'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'As senhas não coincidem',
  path: ['confirmPassword'],
});

export const createGroupSchema = z.object({
  nome: z.string()
    .trim()
    .min(3, 'O nome deve ter pelo menos 3 caracteres')
    .max(50, 'O nome deve ter no máximo 50 caracteres'),
  descricao: z.string()
    .trim()
    .max(200, 'A descrição deve ser breve (máximo 200 caracteres)')
    .optional(),
  regras: z.string()
    .trim()
    .max(2000, 'As regras devem ter no máximo 2000 caracteres')
    .optional(),
  max_members: z.number()
    .int()
    .min(2, 'Mínimo de 2 membros')
    .max(50, 'Máximo de 50 membros')
    .optional(),
});

export const commentSchema = z.object({
  comment_text: z.string()
    .trim()
    .min(1, 'O comentário não pode estar vazio')
    .max(1000, 'O comentário deve ter no máximo 1000 caracteres'),
});

export const createMissionSchema = z.object({
  titulo: z.string()
    .trim()
    .min(3, 'O título deve ter pelo menos 3 caracteres')
    .max(100, 'O título deve ter no máximo 100 caracteres'),
  descricao: z.string()
    .trim()
    .max(500, 'A descrição deve ter no máximo 500 caracteres')
    .optional(),
  tipo: z.enum(['checkin7dias', 'agua_media', 'streak_coletivo', 'metas_concluidas', 'custom']),
  start_date: z.string().datetime(),
  end_date: z.string().datetime(),
  progresso_total: z.number().int().min(1, 'O progresso total deve ser maior que 0'),
  recompensa_pontos: z.number().int().min(0).default(100),
  recompensa_badge_id: z.string().optional(),
}).refine((data) => new Date(data.end_date) > new Date(data.start_date), {
  message: 'A data final deve ser posterior à data inicial',
  path: ['end_date'],
});

export const editProfileSchema = z.object({
  nome: z.string().trim().min(2, 'O nome deve ter pelo menos 2 caracteres').max(100, 'O nome deve ter no máximo 100 caracteres'),
  bio: z.string().max(500, 'A bio deve ter no máximo 500 caracteres').optional().or(z.literal('')),
  sobre_mim: z.string().max(2000, 'O sobre mim deve ter no máximo 2000 caracteres').optional().or(z.literal('')),
  telefone: z.string()
    .optional()
    .or(z.literal(''))
    .transform((val) => val === '' ? undefined : val)
    .refine((val) => {
      if (!val) return true;
      return /^\(\d{2}\) \d{4,5}-\d{4}$/.test(val);
    }, 'Formato inválido. Use (XX) XXXXX-XXXX'),
  data_nascimento: z.string()
    .optional()
    .or(z.literal(''))
    .transform((val) => val === '' ? undefined : val)
    .refine((val) => {
      if (!val) return true;
      return /^\d{4}-\d{2}-\d{2}$/.test(val);
    }, 'Data inválida. Use formato AAAA-MM-DD'),
  cidade_uf: z.string().max(100, 'Cidade/UF deve ter no máximo 100 caracteres').optional().or(z.literal('')),
  empresa: z.string().max(100, 'Empresa deve ter no máximo 100 caracteres').optional().or(z.literal('')),
  cargo: z.string().max(100, 'Cargo deve ter no máximo 100 caracteres').optional().or(z.literal('')),
  website: z.string()
    .optional()
    .or(z.literal(''))
    .transform((val) => {
      if (!val || val === '') return '';
      return val.trim();
    })
    .refine((url) => {
      if (!url || url === '') return true;
      return isValidUrl(url);
    }, 'URL inválida. Ex: seusite.com ou https://seusite.com'),
  linkedin: z.string()
    .optional()
    .or(z.literal(''))
    .transform((val) => {
      if (!val || val === '') return '';
      return val.trim();
    })
    .refine((url) => {
      if (!url || url === '') return true;
      return isValidUrl(url);
    }, 'URL inválida. Ex: linkedin.com/in/usuario'),
  instagram: z.string().optional().or(z.literal('')),
  genero: z.string().optional().or(z.literal('')),
  acorda_hhmm: z.string()
    .optional()
    .or(z.literal(''))
    .transform((val) => val === '' ? undefined : val)
    .refine((val) => {
      if (!val) return true;
      return /^\d{2}:\d{2}$/.test(val);
    }, 'Formato inválido. Use HH:MM'),
  timezone: z.string().optional(),
  language: z.enum(['pt-BR', 'en', 'es']).optional(),
});

export const createGoalSchema = z.object({
  title: z.string()
    .trim()
    .min(3, 'O título deve ter pelo menos 3 caracteres')
    .max(200, 'O título deve ter no máximo 200 caracteres'),
  specific: z.string()
    .trim()
    .min(10, 'Seja mais específico (mínimo 10 caracteres)')
    .max(1000, 'Descrição muito longa (máximo 1000 caracteres)'),
  measurable: z.string()
    .trim()
    .min(10, 'Descreva como vai medir (mínimo 10 caracteres)')
    .max(1000, 'Descrição muito longa (máximo 1000 caracteres)'),
  achievable: z.string()
    .trim()
    .min(10, 'Explique por que é alcançável (mínimo 10 caracteres)')
    .max(1000, 'Descrição muito longa (máximo 1000 caracteres)'),
  relevant: z.string()
    .trim()
    .min(10, 'Explique a relevância (mínimo 10 caracteres)')
    .max(1000, 'Descrição muito longa (máximo 1000 caracteres)'),
  timeBoundISO: z.string()
    .refine((date) => {
      const d = new Date(date);
      return d > new Date();
    }, 'A data deve ser futura'),
});

export const createHabitSchema = z.object({
  title: z.string()
    .trim()
    .min(3, 'O título deve ter pelo menos 3 caracteres')
    .max(100, 'O título deve ter no máximo 100 caracteres'),
  frequency: z.union([
    z.literal('daily'),
    z.literal('weekly'),
    z.literal('monthly')
  ]),
  daysOfWeek: z.array(z.number().min(0).max(6)).optional(),
});

export const challengeJournalSchema = z.object({
  text: z.string()
    .trim()
    .max(2000, 'O texto deve ter no máximo 2000 caracteres')
    .optional(),
  mood: z.union([
    z.literal('muito_ruim'),
    z.literal('ruim'),
    z.literal('neutro'),
    z.literal('bom'),
    z.literal('muito_bom')
  ]).optional(),
  photoUrl: z.string().url('URL inválida').optional(),
});

export type LoginInput = z.infer<typeof loginSchema>;
export type SignupInput = z.infer<typeof signupSchema>;
export type CreateGroupInput = z.infer<typeof createGroupSchema>;
export type CommentInput = z.infer<typeof commentSchema>;
export type CreateMissionInput = z.infer<typeof createMissionSchema>;
export type EditProfileInput = z.infer<typeof editProfileSchema>;
export type CreateGoalInput = z.infer<typeof createGoalSchema>;
export type CreateHabitInput = z.infer<typeof createHabitSchema>;
export type ChallengeJournalInput = z.infer<typeof challengeJournalSchema>;
